import React, { useCallback, useContext, useEffect, useState } from "react";
import SearchResultCard from "./SearchResultCard";
import { Language, PostDetailContext } from "../../App";
import LikeUsersModal from "../../pages/LikeUsersModal";
import { adminBaseURL } from "../../constants/API";
import PostCard from "../../pages/PostCard.jsx";
import axios from "axios";
import { toast } from "react-toastify";
import { reactToPostApi, repostPostApi, savePostApi, usersLikedPostApi } from "../../services/feedApis";
import { authorize } from "../../shared/authorize";
import CommentCanvas from "../../pages/post-details/comment-details/CommentCanvas";
import { LuActivity } from "react-icons/lu";

export default function ClinicalStudiesResults({ data, loading, keyword }) {
  const { lang } = useContext(Language);

  if (!loading && (!data || data.length === 0)) {
    return null;
  }

  return (
    <SearchResultCard
      id="clinical-studies"
      icon={<LuActivity />}
      title={lang?.clinical_studies || "Clinical Studies"}
      count={data?.length}
      link={`/search/clinicals?keyword=${encodeURIComponent(keyword || "")}`}
      seeAll={!loading && data?.length > 2}
    >
      {loading ? (
        <div className="search-posts-list">
          {[1, 2].map((n) => (
            <div key={n} className="search-post-wrapper p-3">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="search-shimmer-box rounded-circle" style={{ width: 44, height: 44, minWidth: 44 }} />
                <div className="flex-grow-1">
                  <div className="search-shimmer-box mb-2" style={{ height: 15, width: "45%" }} />
                  <div className="search-shimmer-box" style={{ height: 12, width: "25%" }} />
                </div>
              </div>
              <div className="search-shimmer-box mb-3" style={{ height: 14, width: "85%" }} />
              <div className="search-shimmer-box rounded-3" style={{ height: 160, width: "100%" }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="search-posts-list">
          {data?.slice(0, 2).map((item, index) => (
            <ClinicalStudiesCard key={item?.id || index} post={item} />
          ))}
        </div>
      )}
    </SearchResultCard>
  );
}

export const ClinicalStudiesCard = ({ post }) => {
  const [data, setData] = useState(post || {});
  const { updatePostDetail } = useContext(PostDetailContext);
  const [likeUsersModal, setLikeUsersModal] = useState(false);
  const [likeSpinner, setLikeSpinner] = useState(false);
  const [likePage, setLikePage] = useState(1);
  const [postId, setPostId] = useState("");
  const [page, setPage] = useState(1);
  const [likeUsers, setLikeUsers] = useState({});
  const [postModal, setPostModal] = useState(false);
  const [postDropDown, setPostDropDown] = useState(false);
  const [dropDownId, setDropDownId] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(-1);

  const userData = localStorage.getItem("SignupData");
  const user = userData ? JSON.parse(userData) : null;

  useEffect(() => {
    setData(post || {});
  }, [post]);

  const handleRepost = async () => {
    try {
      const nextShare = !data.isShare;
      setData((prev) => ({ ...prev, isShare: nextShare }));
      const response = await repostPostApi({ post_id: data?.original_id || data?.id });
      if (response?.status) {
        toast.success(response.action || "Post reposted successfully");
      } else if (response?.action) {
        toast.error(response.action);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSave = async () => {
    try {
      const nextSave = !data.isSave;
      setData((prev) => ({ ...prev, isSave: nextSave }));
      const response = await savePostApi({ post_id: data?.original_id || data?.id });
      if (response?.status) {
        toast.success(response.action || (nextSave ? "Post saved successfully" : "Post removed from saved"));
      } else if (response?.action) {
        toast.error(response.action);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLike = async (post_id, reaction_type = "like") => {
    const prevDataState = data;
    try {
      const wasLiked = Boolean(data.isLike || data.reaction || data.user_reaction);
      const currentReaction = data.reaction || data.user_reaction;
      const isTogglingOff = Boolean(
        wasLiked &&
        currentReaction &&
        String(currentReaction).toLowerCase() === String(reaction_type).toLowerCase()
      );
      const nextIsLiked = !isTogglingOff;
      const nextReaction = isTogglingOff ? null : reaction_type;

      const currentCounts =
        data.reaction_counts && typeof data.reaction_counts === "object"
          ? { ...data.reaction_counts }
          : {};
      if (currentReaction && currentCounts[currentReaction]) {
        currentCounts[currentReaction] = Math.max(
          0,
          Number(currentCounts[currentReaction] || 0) - 1
        );
        if (currentCounts[currentReaction] === 0) {
          delete currentCounts[currentReaction];
        }
      }
      if (nextReaction) {
        currentCounts[nextReaction] =
          (Number(currentCounts[nextReaction]) || 0) + 1;
      }

      let updatedImages = data.like_users_images
        ? [...data.like_users_images]
        : [];
      if (nextIsLiked && !wasLiked && user?.data?.image) {
        updatedImages = [
          user.data.image,
          ...updatedImages.filter((img) => img !== user.data.image),
        ];
      } else if (!nextIsLiked && wasLiked && user?.data?.image) {
        updatedImages = updatedImages.filter(
          (image) => image !== user?.data?.image
        );
      }

      const countDelta =
        nextIsLiked && !wasLiked ? 1 : !nextIsLiked && wasLiked ? -1 : 0;

      const updatedData = {
        ...data,
        isLike: nextIsLiked,
        reaction: nextReaction,
        reaction_counts: currentCounts,
        likes_count: Math.max(0, (data.likes_count || 0) + countDelta),
        like_users_images: updatedImages,
      };
      setData(updatedData);

      await reactToPostApi({
        post_id: post_id || data?.original_id || data?.id,
        reaction_type,
      });
    } catch (error) {
      setData(prevDataState);
      toast.error(error.message);
    }
  };

  const handleLikeUsers = useCallback(
    async (otherId) => {
      setLikeSpinner(true);
      setLikeUsersModal(true);
      try {
        const response = await axios.get(
          `${adminBaseURL}user/post/likes/${otherId}`,
          authorize()
        );
        if (response.data.status) {
          setLikeUsers(response.data.data);
        } else {
          toast.error(response.data.error);
          setLikeUsersModal(false);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLikeSpinner(false);
      }
    },
    []
  );

  const handleNewLikeUsers = useCallback(async () => {
    try {
      const response = await usersLikedPostApi({ post_id: postId, page });
      if (response.data.status) {
        const update = likeUsers.data.concat(response.data.data.data);
        setLikeUsers((prevUsers) => ({ ...prevUsers, data: update }));
        setLikePage((prevPage) => prevPage + 1);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error(error);
    }
  }, [likePage, likeUsers, postId, page]);

  const handleGetComments = useCallback(
    async () => {
      if (updatePostDetail) {
        updatePostDetail(data);
      }
    },
    [data, updatePostDetail]
  );

  return (
    <div className="search-post-wrapper">
      <PostCard
        value={data}
        postDropDown={postDropDown}
        setPostDropDown={setPostDropDown}
        setReportModal={() => {}}
        setDropDownId={setDropDownId}
        dropDownId={dropDownId}
        handleRepost={handleRepost}
        handleSave={handleSave}
        handleFollow={() => {}}
        setShowPost={() => {}}
        editWebinarTab={() => {}}
        handlePostSelect={() => {}}
        updatePostDetail={updatePostDetail}
        handleLikeUsers={handleLikeUsers}
        handleLike={handleLike}
        handleGetComments={handleGetComments}
        setPostModal={setPostModal}
        setPostId={setPostId}
        playingVideo={playingVideo}
        handleVideoPlay={(index) => setPlayingVideo(index)}
      />
      <LikeUsersModal
        isOpen={likeUsersModal}
        toggleLikesModal={() => setLikeUsersModal(!likeUsersModal)}
        likeSpinner={likeSpinner}
        likeUsers={likeUsers}
        handleNewLikeUsers={handleNewLikeUsers}
      />
      {postModal && (
        <CommentCanvas
          postModal={postModal}
          setPostModal={setPostModal}
          postId={postId}
        />
      )}
    </div>
  );
};

